import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { zusToast } from '@zep/toast';
import { type ClassGroupRes } from '@/api/gen/_model/classGroupRes';
import { type SaveContentLinksReq } from '@/api/gen/_model/saveContentLinksReq';
import { ChevronLeftIcon } from '@/icons';
import { CLASSROOM_NAME_MAX_LENGTH } from '@/shared/utils/classroomValidation';
import { confirmDialog, DialogHeader, IconButton, ResponsiveDialog } from '@/ui_v3';
import { TabsWithSlider } from '@/ui_v3/components/tabs';
import { useClassroomLinking, useSaveClassroomLinks } from '../hooks';
import { type ContentShareType } from '../types';
import { type ClassroomSource, trackClassroomEvent } from '../utils';
import { CreateClassroomPanel, MyClassroomsPanel } from './ClassroomLinkPanels';
type LinkTab = 'mine' | 'create';
type LinkChanges = Pick<SaveContentLinksReq, 'linkClassGroupIds' | 'unlinkClassGroupIds' | 'newClassGroupName'>;
interface ClassroomLinkModalProps {
  contentId: string;
  contentType: ContentShareType;
  source: ClassroomSource;
  onClose: () => void;
  /** 있으면 헤더 왼쪽에 「‹」를 두고 누르면 호출한다 (공유 모달/시트에서 열었을 때). */
  onBack?: () => void;
  /** 다른 창에서 바로 넘어올 때 딤이 깜빡이지 않게 등장 애니메이션을 끈다. */
  disableOpenAnimation?: boolean;
}

/**
 * 「클래스 연결」 창. 퀴즈 상세·저장 직후·젤리·공유 어디서 열든 이 창 하나를 쓴다.
 * 클래스 목록을 다 받은 뒤에 폼을 그린다. 그래야 체크·탭의 처음 값을 state 초기값으로
 * 바로 넣을 수 있고, 목록이 오기 전에 「새로 만들기」 탭이 잠깐 보이는 일도 없다.
 */
export function ClassroomLinkModal(props: ClassroomLinkModalProps) {
  const {
    t
  } = useTranslation(['common']);
  const {
    classrooms,
    isLoadingClassrooms,
    isClassroomsError
  } = useClassroomLinking({
    contentId: props.contentId,
    contentType: props.contentType
  });
  const {
    onClose
  } = props;

  // 목록을 못 받으면 「클래스 없음」 폼을 그리지 않고 알린 뒤 닫는다.
  useEffect(() => {
    if (!isClassroomsError) return;
    // 닫히기 전에 다시 그려져도 한 번만 뜨게 id 를 고정한다.
    zusToast.error(t('common:common.error.notLoadContent'), {
      id: 'classroom-link-load-failed'
    });
    onClose();
  }, [isClassroomsError, onClose, t]);
  if (isLoadingClassrooms || isClassroomsError) return null;
  return <ClassroomLinkForm initialClassrooms={classrooms} {...props} data-sentry-element="ClassroomLinkForm" data-sentry-component="ClassroomLinkModal" data-sentry-source-file="ClassroomLinkModal.tsx" />;
}

/**
 * 「내 클래스」에서 체크한 결과(추가·해제)를 「저장」 한 번에 서버로 보내고,
 * 「새로 만들기」는 만들기+연결을 한 번에 보낸다. 둘 다 전부 성공 또는 전부 실패.
 * `initialClassrooms` 는 처음 값으로만 쓴다. 저장하면 창이 닫히고 다시 열 때 새로 시작한다.
 */
function ClassroomLinkForm({
  initialClassrooms,
  contentId,
  contentType,
  source,
  onClose,
  onBack,
  disableOpenAnimation
}: ClassroomLinkModalProps & {
  initialClassrooms: ClassGroupRes[];
}) {
  const {
    t
  } = useTranslation(['common']);
  const {
    save,
    isPending
  } = useSaveClassroomLinks({
    contentId,
    contentType,
    source
  });

  // 처음 열었을 때 연결돼 있던 클래스. 저장할 차이를 구하는 기준이라 바뀌지 않는다.
  const [initialLinked] = useState(() => new Set(initialClassrooms.filter(c => c.isLinked).map(c => c.classGroupId)));
  const [checked, setChecked] = useState(() => new Set(initialLinked));
  const hasClassrooms = initialClassrooms.length > 0;
  const [tab, setTab] = useState<LinkTab>(hasClassrooms ? 'mine' : 'create');
  const [newName, setNewName] = useState('');

  // 옛 연결 창 「클래스 만들기」 클릭과 같은 '생성 시작' 이벤트를 이어 쓴다(시계열 유지).
  // 사용자가 탭을 바꿀 때만 센다. 클래스 0개라 처음부터 열린 「새로 만들기」는 클릭이 아니다.
  // entrySource: 이 창을 연 곳(공유 창·퀴즈 상세 등). 옛 공유 창 만들기 버튼 집계를 여기서 가른다.
  const changeTab = (next: LinkTab) => {
    if (next === 'create' && tab !== 'create') {
      trackClassroomEvent('click_create_classroom', {
        source: 'classroom_link_modal',
        contentId,
        contentType,
        entrySource: source
      });
    }
    setTab(next);
  };
  const linkIds = [...checked].filter(id => !initialLinked.has(id));
  const unlinkIds = [...initialLinked].filter(id => !checked.has(id));
  const trimmedName = newName.trim();
  const toggle = (classGroupId: string) => {
    if (isPending) return;
    // 직전 렌더가 아니라 최신 상태에서 뒤집는다. 연달아 누르면 앞 클릭이 덮어써지던 문제.
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(classGroupId)) next.delete(classGroupId);else next.add(classGroupId);
      return next;
    });
  };
  const submit = async (changes: LinkChanges) => {
    if (isPending) return;
    try {
      await save(changes);
      onClose();
    } catch {
      // 실패 토스트는 useSaveClassroomLinks 가 띄운다. 창과 입력은 그대로 둔다.
    }
  };
  const saveLinks = async () => {
    if (unlinkIds.length > 0) {
      const confirmed = await confirmDialog.openAsync({
        size: 'xs',
        header: {
          title: t('common:classroomLink.unlinkConfirmTitle')
        },
        body: t('common:classroomLink.unlinkConfirmBody'),
        cancelText: t('common:classroomLink.unlinkCancelButton'),
        confirmText: t('common:classroomLink.unlinkConfirmButton'),
        confirmIntent: 'destructive',
        shouldCloseOnDimClick: false
      });
      if (!confirmed) return;
    }
    await submit({
      linkClassGroupIds: linkIds,
      unlinkClassGroupIds: unlinkIds
    });
  };
  const createAndLink = () => submit({
    linkClassGroupIds: [],
    unlinkClassGroupIds: [],
    newClassGroupName: trimmedName
  });
  return <ResponsiveDialog size="md" forceDrawerOnMobile hasDrawerHandle isSwipeDismissible disableOpenAnimation={disableOpenAnimation}
  // 피그마 ClassLinkModal·공유 창(PlaySharePopover) 모두 높이 610 고정. 목록이 남는 높이를 채운다.
  className="h-[610px]" onClose={onClose} data-sentry-element="ResponsiveDialog" data-sentry-component="ClassroomLinkForm" data-sentry-source-file="ClassroomLinkModal.tsx">
      {/* 피그마 ClassLinkModal: 제목 가운데, 왼쪽은 「‹」 자리(없으면 같은 폭 빈칸). */}
      <DialogHeader title={t('common:classroomLink.modalTitle')} hasCloseButton onClose={onClose} className="[&_h2]:flex-1 [&_h2]:text-center" leftSlot={onBack ? <IconButton size="sm" variant="transparent" intent="neutral" className="hover:bg-transparent" aria-label="back" onClick={onBack}>
              <ChevronLeftIcon size="md" />
            </IconButton> : <span aria-hidden className="size-[36px] shrink-0" />} data-sentry-element="DialogHeader" data-sentry-source-file="ClassroomLinkModal.tsx" />
      {/* 피그마 Segmented Control. 빌드 화면 단계 토글(BuildStepToggleTab)과 같은 컴포넌트다. */}
      <div className="flex justify-center px-md pb-sm pt-4xs mobile-lg:px-lg">
        <TabsWithSlider isFull size="sm" value={tab} onValueChange={changeTab} options={[{
        value: 'mine',
        label: t('common:classroom.myClassrooms'),
        disabled: !hasClassrooms
      }, {
        value: 'create',
        label: t('common:classroomLink.createTab')
      }]} className="h-[44px] w-[165px] gap-0 rounded-sm border border-opacity-assistive bg-alternative p-3xs" tabTriggerProps={{
        className: 'h-full text-body-sm-semibold text-assistive data-[state=active]:text-default'
      }} data-sentry-element="TabsWithSlider" data-sentry-source-file="ClassroomLinkModal.tsx" />
      </div>
      {tab === 'mine' ? <MyClassroomsPanel classrooms={initialClassrooms} checked={checked} isPending={isPending} canSave={linkIds.length + unlinkIds.length > 0} onToggle={toggle} onSave={saveLinks} /> : <CreateClassroomPanel name={newName} isPending={isPending} canSave={trimmedName.length > 0 && trimmedName.length <= CLASSROOM_NAME_MAX_LENGTH} onNameChange={setNewName} onSave={createAndLink} />}
    </ResponsiveDialog>;
}
type ShowClassroomLinkModalPayload = Omit<ClassroomLinkModalProps, 'onClose'>;
export function showClassroomLinkModal(payload: ShowClassroomLinkModalPayload) {
  trackClassroomEvent('view_classroom_link_modal', {
    source: payload.source,
    contentId: payload.contentId,
    contentType: payload.contentType
  });
  overlay.open(({
    unmount
  }) => <ClassroomLinkModal {...payload} onClose={unmount} onBack={payload.onBack ? () => {
    unmount();
    payload.onBack?.();
  } : undefined} />);
}